import { useEffect, useState, useMemo } from "react"
import { Form } from "react-bootstrap"
import useUXStore from "../../api/ux/store"
import CStack from "../../components/CStack"
import Loading from "../../components/Loading"
import { useIPStore } from "../../api/ip/store"
import IPBannedItem from "../../containers/Admin/IPBannedItem"
import ButtonControl from "../../components/ButtonControl"
import Label from "../../components/Label"
import Input from "../../components/Input"
import { Col, Row, Stack, Button } from "react-bootstrap"

const defaultErr = { ip: "", reason: "", durationHours: "", description: "" }
const BAN_IP = "banIP"

const AddBanIPView = ({ onAdded = () => { }, initialData }: { onAdded?: () => void, initialData?: any }) => {
    const [adding, setAdding] = useState(false)
    const [banData, setBanData] = useState({
        ip: initialData?.ip || "",
        reason: initialData?.reason || "abuse",
        durationHours: 24,
        isPermanent: initialData ? (!initialData.expiresAt || initialData.expiresAt.startsWith("0001")) : false,
        description: ""
    })
    const [err, setErr] = useState(defaultErr)
    const { refreshDialog, hideDialog } = useUXStore(state => state)
    const { banIP } = useIPStore(state => state)

    const validateIP = (ip: string) => {
        const ipv4Regex = /^(\d{1,3}\.){3}\d{1,3}$/
        const ipv6Regex = /^([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$/
        return ipv4Regex.test(ip) || ipv6Regex.test(ip)
    }

    const onBan = () => {
        setAdding(true)
        refreshDialog({ id: BAN_IP, onClose: undefined }, false)
        setErr(defaultErr)

        if (!validateIP(banData.ip)) {
            setErr({ ...defaultErr, ip: "Invalid IP address format" })
            refreshDialog({ id: BAN_IP }, true)
            setAdding(false)
            return
        }

        const duration = banData.isPermanent ? 0 : banData.durationHours

        banIP(banData.ip, banData.reason, duration, banData.description).then(code => {
            switch (code) {
                case "200":
                    hideDialog(BAN_IP)
                    onAdded()
                    break
                case "conflict":
                    setErr({ ...defaultErr, ip: "Cannot ban a whitelisted IP." })
                    break
                case "incomplete_param":
                    setErr({ ...defaultErr, ip: "Required" })
                    break
                default:
                    console.error(code)
                    break
            }
            if (code !== "200") {
                refreshDialog({ id: BAN_IP }, true)
            }
        }).catch(err => {
            console.error(err)
        }).finally(() => {
            setAdding(false)
        })
    }

    return <>
        <Stack gap={2}>
            <Row>
                <Col><Label>IP Address</Label></Col>
                <Col><Input
                    disabled={adding || !!initialData}
                    label={err.ip}
                    value={banData.ip}
                    error
                    onChange={(e: any) => setBanData({ ...banData, ip: e.target.value })}
                /></Col>
            </Row>
            <Row>
                <Col><Label>Reason</Label></Col>
                <Col>
                    <Form.Select
                        disabled={adding}
                        value={banData.reason}
                        onChange={(e) => setBanData({ ...banData, reason: e.target.value })}
                        style={{ height: '31px', fontSize: '13px' }}
                    >
                        <option value="abuse">Abuse</option>
                        <option value="fraud_suspected">Fraud Suspected</option>
                        <option value="admin_override">Admin Override</option>
                    </Form.Select>
                </Col>
            </Row>
            <Row>
                <Col><Label>Duration (Hours)</Label></Col>
                <Col>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Input
                            type="number"
                            min="1"
                            disabled={adding || banData.isPermanent}
                            value={banData.durationHours.toString()}
                            onChange={(e: any) => setBanData({ ...banData, durationHours: parseInt(e.target.value) || 24 })}
                        />
                        <Form.Check
                            type="checkbox"
                            label="Permanent"
                            disabled={adding}
                            checked={banData.isPermanent}
                            onChange={(e) => setBanData({ ...banData, isPermanent: e.target.checked })}
                            style={{ fontSize: '13px' }}
                        />
                    </div>
                </Col>
            </Row>
            <Row>
                <Col><Label>Description Notes (Optional)</Label></Col>
                <Col><Input
                    disabled={adding}
                    label={err.description}
                    error
                    onChange={(e: any) => setBanData({ ...banData, description: e.target.value })}
                /></Col>
            </Row>
            <Row>
                <Col><Button variant="danger" style={{ float: "right" }} onClick={onBan} disabled={adding}>Ban IP</Button></Col>
            </Row>
        </Stack>
    </>
}

const IPBannedList = () => {
    const { refreshDialog, addToast } = useUXStore(state => state)
    const { bannedLoading, bannedList, retrieveBannedList, unbanIP } = useIPStore(state => state)
    const [sortBy, setSortBy] = useState<'date-newest' | 'date-oldest' | 'reason'>('date-newest')

    useEffect(() => {
        retrieveBannedList()
    }, [])

    const sortedBannedList = useMemo(() => {
        if (!bannedList) return null

        const sorted = [...bannedList]
        switch (sortBy) {
            case 'date-newest':
                sorted.sort((a, b) => new Date(b.bannedAt).getTime() - new Date(a.bannedAt).getTime())
                break
            case 'date-oldest':
                sorted.sort((a, b) => new Date(a.bannedAt).getTime() - new Date(b.bannedAt).getTime())
                break
            case 'reason':
                sorted.sort((a, b) => a.reason.localeCompare(b.reason))
                break
        }
        return sorted
    }, [bannedList, sortBy])

    const onUnban = (ip: string, reason: string) => {
        refreshDialog({
            id: "unbanIP",
            title: "Unban IP Address",
            visible: true,
            content: (
                <div>
                    Are you sure you want to unban <b>{ip}</b>?<br />
                    <br />
                    This IP was banned for: <b>{{
                        'credential_stuffing': 'Credential Stuffing',
                        'expired_session': 'Expired Session Abuse',
                        'failed_password': 'Failed Password',
                        'rate_limit': 'Rate Limiting',
                    }[reason] ?? reason}</b><br />
                    <br />
                    <span style={{ color: "#dc3545" }}>Warning: This IP may resume the activity that caused the ban.</span>
                </div>
            ),
            onConfirm() {
                unbanIP(ip)
                    .then(code => {
                        switch (code) {
                            case "200":
                                addToast("", "IP unbanned successfully!")
                                break;
                            default:
                                addToast("Failed", "Failed to unban IP!")
                                break;
                        }
                    })
                    .catch(err => {
                        console.error(err)
                    })
            },
        }, true)
    }

    const onAdd = () => {
        refreshDialog({
            id: BAN_IP,
            title: "Manual Ban",
            visible: true,
            content: <AddBanIPView onAdded={() => retrieveBannedList()} />,
            onConfirm: undefined,
        }, true)
    }

    const onEdit = (entry: any) => {
        refreshDialog({
            id: BAN_IP,
            title: "Edit Ban Configuration",
            visible: true,
            content: <AddBanIPView initialData={entry} onAdded={() => retrieveBannedList()} />,
            onConfirm: undefined,
        }, true)
    }

    if (bannedLoading) return <Loading center />

    return <>
        <ButtonControl text="Manual Ban" buttonLabel="Ban IP" variant="outline-danger" onClicked={onAdd} />
        <div className="mt-3 mb-2" style={{ fontSize: '13px', color: '#666' }}>
            <span className="text-danger">*</span> This feature is specific to CoreOne. It allows manual banning of IP addresses to prevent unauthorized access or abuse.
        </div>
        <CStack header={`IP Addresses${sortedBannedList && sortedBannedList.length > 0 ? ` (${sortedBannedList.length})` : ''}`}>
            {sortedBannedList && sortedBannedList.length > 0 && (
                <div style={{ padding: "10px", borderBottom: "1px solid #e0e0e0" }}>
                    <Form.Group style={{ marginBottom: 0 }}>
                        <Form.Label style={{ marginRight: "10px", fontWeight: "normal" }}>Sort by:</Form.Label>
                        <Form.Select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value as any)}
                            style={{ width: "auto", display: "inline-block" }}
                        >
                            <option value="date-newest">Date (Newest First)</option>
                            <option value="date-oldest">Date (Oldest First)</option>
                            <option value="reason">Reason</option>
                        </Form.Select>
                    </Form.Group>
                </div>
            )}
            {
                sortedBannedList !== null && sortedBannedList.length === 0 &&
                <div style={{ padding: "20px", textAlign: "center", color: "#666" }}>
                    No banned IPs
                </div>
            }
            {
                sortedBannedList !== null && sortedBannedList!.map((item, i) =>
                    <IPBannedItem
                        key={"banned" + item.ip}
                        entry={item}
                        onUnban={onUnban}
                        onEdit={onEdit}
                    />)
            }
        </CStack>
    </>
}

export default IPBannedList